<template>
  <div class="input-group">
    <div v-if="property.tts" class="p-b-20">
      <div class="input-title">Text-to-Speech</div>
      <v-row class="m-l-align">
        <v-col class="m-t-5" cols="12">
          <v-autocomplete
            class="m-t-5 no-drag"
            v-model="property.tts.voice"
            :items="ttsChoices"
            item-text="allText"
            item-value="voice"
            label="Voice"
            placeholder="Voice"
            dense
            hide-details
          >
            <template slot="selection" slot-scope="{ item }">
              {{ subString(item.name, 70) }}
            </template>
            <template slot="item" slot-scope="{ item }">
              <div
                class="float-right width100 font-size-sub margin8px m-r-5 row-center"
              >
                <div>
                  <img
                    v-if="item.icon"
                    :src="item.icon"
                    :alt="item.engine"
                    width="20"
                    class="m-r-15"
                  />
                </div>
                <div>
                  <div class="dropdown-title">
                    {{ subString(item.name, 50) }}
                  </div>
                  <div class="dropdown-text">
                    {{ subString(item.description, 70) }}
                  </div>
                </div>
              </div>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col cols="2" class="p-t-0 p-b-0">
          Speed
        </v-col>
        <v-col cols="10" class="p-t-0 p-b-0">
          <div class="d-flex align-center justify-start">
            <v-slider
              v-model="property.tts.speed"
              :min="minSpeed"
              :max="maxSpeed"
              :step="stepSpeed"
              hide-details
              class="slider-scroll"
            ></v-slider>
            <span class="slider-text m-l-20">
              {{ property.tts.speed ? property.tts.speed.toFixed(2) : '' }}
            </span>
          </div>
        </v-col>
        <v-col
          v-if="!['botnoi', 'botnoiv3'].includes(engine)"
          cols="2"
          class="p-t-0"
        >
          Pitch
        </v-col>
        <v-col
          v-if="!['botnoi', 'botnoiv3'].includes(engine)"
          cols="10"
          class="p-t-0"
        >
          <div class="d-flex align-center justify-start">
            <v-slider
              v-model="property.tts.pitch"
              :min="minPitch"
              :max="maxPitch"
              :step="stepPitch"
              hide-details
              class="slider-scroll"
            ></v-slider>
            <span class="slider-text m-l-20">
              {{ property.tts.pitch ? property.tts.pitch.toFixed(2) : '' }}
            </span>
          </div>
        </v-col>
        <v-col cols="12" class="p-t-0 pb-5">
          <v-select
            v-model="property.tts.dictionaries"
            label="Dictionaries"
            :items="ttsDictionaries"
            item-text="name"
            item-value="tts_dictionary_id"
            dense
            hide-details
            multiple
            chips
            clearable
            clear-icon="fa-times"
          >
            <template v-slot:selection="data">
              <v-tooltip
                v-if="data.item.description"
                :max-width="350"
                :open-delay="700"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip v-on="on" v-bind="attrs">{{ data.item.name }}</v-chip>
                </template>
                <div class="text-block">{{ data.item.description }}</div>
              </v-tooltip>
              <v-chip v-else>
                {{ data.item.name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
              <v-tooltip
                v-if="data.item.description"
                :max-width="350"
                :open-delay="700"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-layout wrap v-on="on" v-bind="attrs">
                    <v-list-item-action class="m-r-10">
                      <v-checkbox v-model="data.attrs.inputValue" />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-layout>
                </template>
                <div class="text-block">{{ data.item.description }}</div>
              </v-tooltip>
              <v-layout v-else wrap>
                <v-list-item-action class="m-r-10">
                  <v-checkbox v-model="data.attrs.inputValue" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                </v-list-item-content>
              </v-layout>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="d-flex align-start p-t-0 p-b-0" cols="12" sm="12" lg="12">
          <v-textarea
            v-model="text"
            label="Enter text"
            placeholder="Enter text to speak …"
            class="textarea-input no-drag"
            solo
            hide-details
            rows="2"
            row-height="20"
          ></v-textarea>
          <v-icon class="m-l-10" small :disabled="!text" @click="playPrompt">
            {{ isPlaying ? 'fa-stop-circle' : 'fa-play-circle' }}
          </v-icon>
        </v-col>
      </v-row>
    </div>

    <div class="m-t-15">
      <config-nlu :defaultInput="property.default_input"></config-nlu>
    </div>

    <div class="m-t-15">
      <div class="input-title">Flow</div>
      <v-row class="m-l-align">
        <v-col cols="7">
          <span>Fallback after</span>
        </v-col>
        <v-col class="d-flex" cols="5">
          <div>
            <v-text-field
              v-model="property.flow.fallback_after"
              type="number"
              step="1"
              min="0"
              dense
              reverse
              hide-details
              class="margin0 padding0 width50px"
            ></v-text-field>
          </div>
          <div class="ml-2">attempts</div>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-0" cols="7">
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <div v-on="on">Timeout after</div>
            </template>
            <span>Duration when no response is detected.</span>
          </v-tooltip>
        </v-col>
        <v-col class="d-flex p-t-5 p-b-0" cols="5">
          <div>
            <v-text-field
              v-model="property.flow.fallback_timeout"
              type="number"
              step="5"
              min="5"
              dense
              reverse
              hide-details
              class="margin0 padding0 width50px"
            ></v-text-field>
          </div>
          <div class="ml-2">minutes</div>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-1" cols="12" sm="12" lg="12">
          <v-select
            class="m-auto"
            v-model="property.flow.page_max_retry"
            :items="tabFilter"
            item-text="name"
            item-value="id"
            label="On max retry, jump to page"
            dense
            hide-details
            clearable
            clear-icon="fa-times"
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-0" cols="12" sm="12" lg="12">
          <v-select
            class="m-auto"
            v-model="property.flow.page_error"
            :items="tabFilter"
            item-text="name"
            item-value="id"
            label="On error, jump to page"
            dense
            hide-details
            clearable
            clear-icon="fa-times"
          ></v-select>
        </v-col>
      </v-row>
      <v-row class="m-l-align">
        <v-col class="p-t-0" cols="12" sm="12" lg="12">
          <v-select
            class="m-auto"
            v-model="property.flow.page_time_out"
            :items="tabFilter"
            item-text="name"
            item-value="id"
            label="On error timeout, jump to page"
            dense
            hide-details
            clearable
            clear-icon="fa-times"
          ></v-select>
        </v-col>
      </v-row>
    </div>

    <div class="m-t-15">
      <div class="input-title">Not available</div>
      <v-row class="m-l-align">
        <v-col cols="12">
          <v-textarea
            v-model="property.flow.data_not_available"
            label="Message for data not available"
            class="textarea-input no-drag"
            solo
            hide-details
            rows="2"
            row-height="20"
          ></v-textarea>
        </v-col>
      </v-row>
    </div>

    <audio ref="myAudio" autoload>
      <source :src="previewVoice" type="audio/wav" />
      Your browser does not support the audio element.
    </audio>

    <hr class="note-divider" />
    <note-component :node="node" />
  </div>
</template>

<script>
import _ from 'lodash'
import apiSynthesis from '@/apis/synthesis'
import apiTtsDictionaries from '@/apis/tts_dictionaries'
import NoteComponent from '../../../components/note'
import ConfigNlu from '../../voice/start/components/config_nlu'

export default {
  props: {
    node: Object,
    tabs: Array,
    ttsChoices: Array
  },
  components: {
    NoteComponent,
    ConfigNlu
  },
  data() {
    return {
      property: {},
      tabFilter: [],
      defaultText: 'ยินดีต้อนรับสู่บริการ virtual agent',
      text: '',
      defaultSpeed: 1,
      minSpeed: 0.5,
      maxSpeed: 1.5,
      stepSpeed: 0.05,
      defaultPitch: 1,
      minPitch: 0.5,
      maxPitch: 1.5,
      stepPitch: 0.05,
      isPlaying: false,
      previewVoice: '',
      engine: '',
      ttsDictionaries: []
    }
  },
  watch: {
    'property.tts.voice': {
      immediate: true,
      handler(value) {
        if (value) {
          const item = _.find(this.ttsChoices, { voice: value })
          this.engine = item ? item.engine.toLowerCase() : ''
          if (!['botnoi', 'botnoiv3'].includes(this.engine)) {
            if (!this.property.tts.pitch) {
              this.property.tts.pitch = this.defaultPitch
            }
          } else {
            this.property.tts.pitch = undefined
          }
          this.text = item.sample_utterance || this.defaultText
        } else {
          const item = _.find(this.ttsChoices, { isDefault: true })
          if (item && this.property.tts) this.property.tts.voice = item.voice
        }
      }
    },
    'property.tts.dictionaries': {
      handler(value) {
        if (this.ttsDictionaries.length > 0) this.setTtsDictionariesName()
      }
    }
  },
  created() {
    if (!this.node.property.tts) {
      this.node.property.tts = {
        voice: '',
        speaker: '',
        speed: 1,
        pitch: 1,
        dictionaries: [],
        dictionaries_name: []
      }
    }
    if (!this.node.property.tts.dictionaries) {
      this.node.property.tts.dictionaries = []
    }
    if (!this.node.property.tts.dictionaries_name) {
      this.node.property.tts.dictionaries_name = []
    }
    if (!this.node.property.default_input) {
      this.node.property.default_input = {
        is_count_retry: false,
        nlu: ''
      }
    }
    if (!this.node.property.default_input.is_count_retry) {
      this.node.property.default_input.is_count_retry = false
    }
    if (!this.node.property.default_input.nlu) {
      this.node.property.default_input.nlu = ''
    }

    this.property = this.node.property

    const tabFilter = _.filter(this.tabs, o => o.name !== 'Home')
    this.tabFilter = _.sortBy(tabFilter, [
      function(o) {
        return o.name
      }
    ])

    this.getTtsDictionaries()
  },
  mounted() {
    this.$refs.myAudio.addEventListener('play', () => {
      this.isPlaying = true
    })
    this.$refs.myAudio.addEventListener('ended', () => {
      this.isPlaying = false
    })
  },
  methods: {
    async playPrompt() {
      try {
        if (!this.isPlaying) {
          const tts = await apiSynthesis.synthesis({
            ...this.property.tts,
            text: this.text,
            useCache: false
          })
          this.previewVoice = tts.data.audio

          this.$refs.myAudio.load()

          setTimeout(() => {
            this.$refs.myAudio.play()
          }, 300)
        } else {
          this.$refs.myAudio.pause()
          this.isPlaying = false
        }
      } catch {}
    },
    async getTtsDictionaries() {
      try {
        const ttsDictionaries = await apiTtsDictionaries.getTtsDictionaryList()
        this.ttsDictionaries = ttsDictionaries.data

        this.checkTtsDictionaries()
      } catch {}
    },
    checkTtsDictionaries() {
      if (this.property.tts.dictionaries.length) {
        for (let i = this.property.tts.dictionaries.length - 1; i >= 0; i--) {
          const tts_dictionary_id = this.property.tts.dictionaries[i]
          const ttsDictionary = _.find(this.ttsDictionaries, {
            tts_dictionary_id
          })
          if (!ttsDictionary) this.property.tts.dictionaries.splice(i, 1)
        }
      }
    },
    setTtsDictionariesName() {
      this.property.tts.dictionaries_name = []
      this.property.tts.dictionaries.forEach(id => {
        const name = _.find(this.ttsDictionaries, { tts_dictionary_id: id })
          .name
        if (name) this.property.tts.dictionaries_name.push(name)
      })
    },
    isSubPlaying() {
      this.$emit('isSubPlaying', null)
    },
    subString(text, length) {
      if (text.length < length) return text
      return `${text.substr(0, length)}...`
    },
    setPropertyData(property) {
      let val = this.property.flow[property]
      if (val < 0) val = 0
      if (property === 'asr_confidence_threshold' && val > 1) val = 1
      this.property.flow[property] = parseFloat(val).toFixed(2)
    }
  }
}
</script>

<style scoped lang="scss">
.display-inline-flex {
  display: inline-flex;
}
::v-deep .v-text-field__slot > input,
::v-deep .v-text-field__slot > .v-select__slot {
  margin: 0px !important;
  padding: 0px !important;
}

.width50px {
  width: 50px;
}

.font-size-body {
  max-width: 300px;
  display: flex;
  text-align: justify;
  text-justify: inter-word;
  padding-left: 4px;
  margin-left: 4px;
  padding-right: 4px;
  margin-right: 4px;
  margin-bottom: 0px;
  word-wrap: break-word;
}

.row-center {
  display: flex;
  align-items: center;
}

.dropdown-title {
  font-size: 14px;
}

.dropdown-text {
  font-size: 11px;
  color: $text-color;
}

.slider-scroll {
  width: 290px;
  max-width: 290px;
}

.slider-text {
  width: 20px;
}

.m-l-align {
  margin-left: 20px;
}
</style>
